/**
 * Central image registry. Swap file paths here when real photography arrives.
 * Every entry carries its intrinsic size so layouts never shift.
 */
import heroRoof from "@/assets/hero-roof.jpg";
import systemShingles from "@/assets/system-shingles.jpg";
import systemMetal from "@/assets/system-metal.jpg";
import systemFlat from "@/assets/system-flat.jpg";

export type ImageAsset = {
  src: string;
  width: number;
  height: number;
  alt: string;
};

/**
 * Homepage hero motion background.
 * Multi-shot cinematic Ken Burns sequence assembled from photoreal stills
 * (this environment cannot generate video under its ZDR policy).
 */
export const heroVideo = {
  src: "/media/hero-video/hero-roof-loop.mp4",
  poster: "/media/hero-video/01-asphalt-establishing.jpg",
  // Re-encoded 2026-08: 1280x720 H.264 CRF30 (~1.8 MB, was 16.4 MB 1080p).
  width: 1280,
  height: 720,
  alt: "Cinematic sequence of a luxury West Vancouver home roof transformation to standing-seam steel",
} as const;

const SERVICE_HERO_SIZE = { width: 1536, height: 1024 } as const;

function serviceHero(slug: string, alt: string): ImageAsset {
  return {
    src: `/media/services/${slug}-hero.jpg`,
    ...SERVICE_HERO_SIZE,
    alt,
  };
}

export const images = {
  hero: {
    src: heroRoof,
    width: 1920,
    height: 1280,
    alt: "Modern home with a dark standing-seam metal roof at dusk",
  },
  shingles: {
    src: systemShingles,
    width: 1024,
    height: 768,
    alt: "Close view of charcoal architectural asphalt shingles on a steep residential roof plane",
  },
  metal: {
    src: systemMetal,
    width: 1024,
    height: 768,
    alt: "Detail of dark standing-seam metal roofing panels and ridge flashing",
  },
  flat: {
    src: systemFlat,
    width: 1024,
    height: 768,
    alt: "Overhead view of a low-slope membrane roof with a skylight curb and roof drain",
  },

  "emergency-roof-leak-repair": serviceHero(
    "emergency-roof-leak-repair",
    "Wet asphalt shingles and stained stucco wall below a failed plumbing vent boot after rain on a Metro Vancouver bungalow",
  ),
  "flashing-roof-penetration-repair": serviceHero(
    "flashing-roof-penetration-repair",
    "Galvanized pipe boot and step flashing integrated into dark architectural shingles at a roof penetration",
  ),
  "moss-removal-roof-gutter": serviceHero(
    "moss-removal-roof-gutter",
    "Thick green moss covering the north slope of an aged asphalt shingle roof on a Coquitlam home",
  ),
  "asphalt-shingle-replacement": serviceHero(
    "asphalt-shingle-replacement",
    "Craftsman bungalow with a newly installed dark charcoal architectural asphalt shingle roof",
  ),
  "roof-health-check-maintenance": serviceHero(
    "roof-health-check-maintenance",
    "Ladder at the eave of a suburban home with clipboard and laptop ready for a roof condition assessment",
  ),
  "shingle-spot-repair": serviceHero(
    "shingle-spot-repair",
    "Localized new charcoal shingles patched into a weathered asphalt roof field near the eave",
  ),
  "gutter-cleaning-downspout-flush": serviceHero(
    "gutter-cleaning-downspout-flush",
    "White K-style gutter clogged with wet maple leaves and moss above a downspout inlet",
  ),
  "storm-tarping-emergency-dry-in": serviceHero(
    "storm-tarping-emergency-dry-in",
    "Blue emergency tarp secured with battens and sandbags over storm damage on a Port Moody roof",
  ),
  "skylight-leak-flashing-repair": serviceHero(
    "skylight-leak-flashing-repair",
    "Skylight curb with metal step flashing woven into surrounding asphalt shingles",
  ),
  "chimney-flashing-repair": serviceHero(
    "chimney-flashing-repair",
    "Brick chimney step flashing and counter-flashing layered correctly with asphalt shingles",
  ),
  "gutter-replacement": serviceHero(
    "gutter-replacement",
    "Newly installed white aluminum gutters and downspout along the fascia of a Surrey home",
  ),
  "gutter-guard-installation": serviceHero(
    "gutter-guard-installation",
    "Micro-mesh gutter guard fitted over a white aluminum gutter beneath dark asphalt shingles",
  ),
  "roof-ventilation-upgrade": serviceHero(
    "roof-ventilation-upgrade",
    "Continuous ridge vent installed along the peak of a dark charcoal asphalt shingle roof",
  ),
  "fascia-soffit-repair-replacement": serviceHero(
    "fascia-soffit-repair-replacement",
    "New white fascia board and perforated soffit panels under a dark asphalt shingle overhang",
  ),
  "cedar-shake-repair-restoration": serviceHero(
    "cedar-shake-repair-restoration",
    "New cedar shakes woven into a weathered silver-gray cedar roof field with proper coursing",
  ),
  "flat-low-slope-roof-repair": serviceHero(
    "flat-low-slope-roof-repair",
    "Low-slope SBS membrane roof with a freshly repaired seam near a roof drain",
  ),
  "metal-roof-repair-maintenance": serviceHero(
    "metal-roof-repair-maintenance",
    "Dark standing-seam metal roof panels and ridge detail on a residential hillside home",
  ),
  "cedar-shake-roof-replacement": serviceHero(
    "cedar-shake-roof-replacement",
    "West Vancouver home with a complete new warm-tone cedar shake roof under overcast light",
  ),
  "metal-roof-replacement": serviceHero(
    "metal-roof-replacement",
    "Contemporary North Vancouver home with a new charcoal standing-seam metal roof",
  ),
  "flat-roof-replacement-sbs-tpo-pvc": serviceHero(
    "flat-roof-replacement-sbs-tpo-pvc",
    "Completed white TPO membrane roof with clean welded seams, drains, and flashed equipment curb",
  ),
} satisfies Record<string, ImageAsset>;
